import { useCallback, useEffect, useMemo, useState } from "react";
import { Empty, Spin } from "antd";
import { FiActivity } from "react-icons/fi";
import TableWithSkeleton from "../../../components/global/table/TableWithSkeleton.jsx";
import { getResponseData } from "../../../utils/apiHelpers.js";
import { GetOfficeGoalsReportApi } from "../../../api/request/salesDashboard/reports.js";
import {
  GetAllOffices,
  GetAllTeamLeaders,
} from "../../../api/request/subadmin.js";
import {
  GetSalesAgentsApi,
  buildSalesAgentsParams,
  mapSalesAgentsToOptions,
} from "../../../api/request/salesDashboard/dashboard.js";
import DashboardFilters from "../dashboard/components/DashboardFilters.jsx";
import { getOfficeGoalsReportColumns } from "../../../columns/salesDashboard/reports/officeGoalsReportColumns.jsx";
import { useUserStore } from "../../../store/userStore.js";

const formatMoney = (value) => `$${Number(value || 0).toLocaleString("en-US")}`;
const formatNumber = (value, digits = 2) =>
  Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: digits });

const buildQuery = (params) =>
  new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== undefined && value !== null && value !== "",
      ),
    ),
  ).toString();

const OfficeGoalsReport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const userDetails = useUserStore((state) => state.userDetails);
  const isSales = userDetails?.department === "Sales";
  const isAgent = isSales && !userDetails?.isTeamLeader;
  const isTeamLeader = isSales && userDetails?.isTeamLeader;
  const teamLeadId = isSales ? userDetails?.userId : undefined;
  const [offices, setOffices] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filters, setFilters] = useState({
    from: undefined,
    to: undefined,
    officeId: undefined,
    teamLeadId: teamLeadId,
    agentId: undefined,
  });
  const [leaderboard, setLeaderboard] = useState([]);
  const [tableRows, setTableRows] = useState([]);

  const fetchOffices = async () => {
    try {
      const data = getResponseData(await GetAllOffices());
      if (data?.success) {
        setOffices(
          (data?.data || []).map((item) => ({
            label: item?.officeName || item?.name,
            value: item?.id || item?.officeId,
          })),
        );
      }
    } catch (error) {
      setOffices([]);
    }
  };

  const fetchTeamLeaders = async () => {
    if (!filters.officeId) {
      setTeamLeaders([]);
      return;
    }
    try {
      const data = getResponseData(await GetAllTeamLeaders(filters.officeId));
      if (data?.success) {
        setTeamLeaders(
          (data?.data || []).map((item) => ({
            label: item?.fullName || item?.teamLeadName,
            value: item?.userId || item?.teamLeadId,
          })),
        );
      }
    } catch (error) {
      setTeamLeaders([]);
    }
  };

  const fetchAgents = async () => {
    if (!filters.teamLeadId) {
      setAgents([]);
      return;
    }
    try {
      const data = getResponseData(
        await GetSalesAgentsApi(buildSalesAgentsParams(filters)),
      );
      if (data?.success) {
        setAgents(mapSalesAgentsToOptions(data?.data));
      }
    } catch (error) {
      setAgents([]);
    }
  };

  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    try {
      const query = buildQuery(filters);
      const data = getResponseData(await GetOfficeGoalsReportApi(query));
      if (data?.success) {
        setLeaderboard(data?.data?.officeLeaderboard || []);
        setTableRows(data?.data?.officeMetrics || []);
      } else {
        setLeaderboard([]);
        setTableRows([]);
      }
    } catch (error) {
      setLeaderboard([]);
      setTableRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchOffices();
  }, []);

  useEffect(() => {
    fetchTeamLeaders();
  }, [filters.officeId]);

  useEffect(() => {
    fetchAgents();
  }, [filters.teamLeadId, filters.agentId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const maxDeposits = useMemo(
    () =>
      Math.max(0, ...leaderboard.map((item) => Number(item?.deposits || 0))),
    [leaderboard],
  );

  const columns = useMemo(
    () => getOfficeGoalsReportColumns({ formatMoney, formatNumber }),
    [formatMoney, formatNumber],
  );

  return (
    <div className="space-y-4">
      <DashboardFilters
        isSales={isSales}
        isTeamLeader={isTeamLeader}
        isAgent={isAgent}
        filters={filters}
        setFilters={setFilters}
        offices={offices}
        teamLeaders={teamLeaders}
        agents={agents}
      />

      {isLoading ? (
        <div className="flex justify-center flex-col my-20">
          <Spin size="large" />
          <p className="primary text-center mt-2">Loading...</p>
        </div>
      ) : (
        <>
          <div className="border-2 border-light rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-light flex items-center justify-between">
              <p className="text-title font-semibold">
                Office Leaderboard
              </p>
              <span className="text-xs bg-cyan-100 text-cyan-600 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                <FiActivity size={12} />
                Live ranking
              </span>
            </div>
            <div className="p-4 space-y-8">
              {leaderboard?.length ? (
                leaderboard.map((item, idx) => {
                  const ratio =
                    maxDeposits > 0
                      ? (Number(item?.deposits || 0) / maxDeposits) * 100
                      : 0;
                  return (
                    <div
                      key={`${item?.officeId || item?.officeName || idx}`}
                      className="flex items-center gap-3"
                    >
                      <span className="h-10 w-10 shrink-0 rounded-lg bg-secondaryBackground text-title font-semibold flex items-center justify-center">
                        #{item?.rank || idx + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3 mb-1">
                          <p className="font-semibold text-title truncate">
                            {item?.officeName || "--"}
                          </p>
                          <p className="text-gray-500 text-sm shrink-0">
                            {formatMoney(item?.deposits)} -{" "}
                            {formatNumber(item?.ftd, 0)} FTD -{" "}
                            Vol. {formatMoney(item?.volume)}
                          </p>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-blue-600"
                            style={{ width: `${Math.max(ratio, 4)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="py-6">
                  <Empty description="No data found" />
                </div>
              )}
            </div>
          </div>

          <div className="border-2 border-light rounded-xl p-4 overflow-hidden">
            <p className="text-title font-semibold mb-3">
              Office Metrics
            </p>
            <div className="overflow-hidden rounded-xl">
              <TableWithSkeleton
                showIndexColumn={false}
                pagination={false}
                rowKey="officeId"
                columns={columns}
                data={tableRows}
                loading={isLoading}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OfficeGoalsReport;
